import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { decryptIdFromUrl, encryptIdForUrl } from "@/utils/urlCrypto";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Building2,
  Search,
  Filter,
  FileText,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Eye,
  MoreVertical,
  ExternalLink,
  CheckCircle,
  XCircle,
  Globe,
  Edit2,
  Archive,
  RotateCcw,
  Loader2,
  Clock,
  ImageIcon,
  Users,
  Tag,
  Shield,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { cn } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/media";
import { SolicitacaoStatusBadge } from "@/components/solicitacoes/SolicitacaoStatusBadge";
import { NotFoundState } from "@/components/ui/empty-state";
import type { StatusSolicitacao } from "@/types/solicitacao";
import { Logo, FotoPerfil } from "@/types/media";
import { AppConfig } from "@/datasources/AppConfig";

interface Endereco {
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  pais?: string;
  ibge?: string;
}

interface AgenteCompacto {
  id: string;
  nome: string;
  email?: string | null;
  fotoPerfil?: FotoPerfil | null;
  arquivado: boolean;
}

interface Ouvidoria {
  id: string;
  nome: string;
  cnpj: string;
  arquivada: boolean;
  geral: boolean;
  logo?: Logo | null;
  razaoSocial?: string | null;
  naturezaJuridica?: string | null;
  nomeFantasia?: string | null;
  email?: string | null;
  telefone?: string | null;
  integracao?: string | null;
  etiquetas?: string[] | null;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  endereco?: Endereco | null;
  agentes?: AgenteCompacto[] | null;
  status?: "Aguardando Aprovação" | "Aprovada" | "Reprovada" | null;
}

interface Anexo {
  id: string;
  arquivo: string;
}

interface SolicitacaoOuvidoria {
  id: string;
  topico: {
    id: string;
    descricao: string;
  };
  upn: string;
  status: string;
  estadoAprovacao: string;
  dataCriacao: string;
  totalApoiadores: number;
  arquivada: boolean;
  anexos?: Anexo[];
}


export default function OuvidoriaSupervisionadaDetalhesPage() {
  const { id: encryptedId } = useParams<{ id: string }>();
  const [decryptedId, setDecryptedId] = useState<string>("");
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [ouvidoria, setOuvidoria] = useState<Ouvidoria | null>(null);
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoOuvidoria[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSolicitacoes, setIsLoadingSolicitacoes] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Decrypt ID from URL
  useEffect(() => {
    const decryptId = async () => {
      if (encryptedId) {
        const decrypted = await decryptIdFromUrl(encryptedId);
        setDecryptedId(decrypted);
      }
    };
    decryptId();
  }, [encryptedId]);

  const fetchOuvidoria = useCallback(async () => {
    if (!decryptedId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(
        `https://api.toolbox.app.br/urbnet/v1/municipios/${AppConfig.municipioId}/ouvidorias/${decryptedId}`,
        {
          headers: {
            'Accept': 'application/vnd.data.full.v1+json',
            'Authorization': `Bearer ${AppConfig.tokenAcesso}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const ouvidoriaData = Array.isArray(data.data) ? data.data[0] : Array.isArray(data.item) ? data.item[0] : Array.isArray(data) ? data[0] : data;
      setOuvidoria(ouvidoriaData);
    } catch (err) {
      setError("Não foi possível carregar os dados da ouvidoria.");
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar os dados da ouvidoria.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [decryptedId, toast]);

  const fetchSolicitacoes = useCallback(async () => {
    if (!decryptedId) return;
    
    try {
      setIsLoadingSolicitacoes(true);
      
      const response = await fetch(
        `https://api.toolbox.app.br/urbnet/v1/municipios/${AppConfig.municipioId}/ouvidorias/${decryptedId}/solicitacoes`,
        {
          headers: {
            'Accept': 'application/vnd.data.partial.v1+json',
            'Authorization': `Bearer ${AppConfig.tokenAcesso}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setSolicitacoes(data.data || data || []);
    } catch (err) {
      setSolicitacoes([]);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar as solicitações da ouvidoria.",
      });
    } finally {
      setIsLoadingSolicitacoes(false);
    }
  }, [decryptedId, toast]);

  useEffect(() => {
    fetchOuvidoria();
    fetchSolicitacoes();
  }, [fetchOuvidoria, fetchSolicitacoes]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega os dados
  useEffect(() => {
    const handleFocus = () => {
      fetchOuvidoria();
      fetchSolicitacoes();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchOuvidoria();
        fetchSolicitacoes();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchOuvidoria, fetchSolicitacoes]);

  const handleSolicitacaoClick = async (id: string) => {
    const encryptedId = await encryptIdForUrl(id);
    navigate(`/solicitacoes/${encryptedId}`);
  };

  // Early return if no ID
  if (!decryptedId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <NotFoundState
          resourceName="ouvidoria supervisionada"
          onGoBack={() => navigate("/ouvidorias-supervisionadas")}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <NotFoundState
          resourceName="ouvidoria supervisionada"
          onGoBack={() => navigate("/ouvidorias-supervisionadas")}
        />
      </div>
    );
  }

  if (!ouvidoria) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <NotFoundState
          resourceName="ouvidoria supervisionada"
          onGoBack={() => navigate("/ouvidorias-supervisionadas")}
        />
      </div>
    );
  }

  const avatarFallback = ouvidoria.nome ? ouvidoria.nome.slice(0, 2).toUpperCase() : "OU";

  const isAguardandoAprovacao = ouvidoria.status === "Aguardando Aprovação";
  const isReprovada = ouvidoria.status === "Reprovada";

  return (
    <div className="space-y-6">
      {/* Banner de aviso para ouvidorias com status especial */}
      {(isAguardandoAprovacao || isReprovada) && (
        <div className="border-b border-warning/30 bg-warning/10 text-warning-foreground">
          <div className="flex items-start gap-3 px-4 sm:px-6 py-3">
            <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-warning">
                {isAguardandoAprovacao
                  ? "Esta ouvidoria supervisionada está aguardando aprovação"
                  : "Esta ouvidoria supervisionada foi reprovada"}
              </p>
              <p className="text-muted-foreground mt-1">
                {isAguardandoAprovacao
                  ? "Esta ouvidoria não poderá receber solicitações até ser aprovada pela ouvidoria geral do município."
                  : "Esta ouvidoria não poderá receber solicitações pois foi reprovada pela ouvidoria geral do município."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate("/ouvidorias-supervisionadas")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para Ouvidorias Supervisionadas
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <Avatar className="h-24 w-24 mb-4">
                  {ouvidoria.logo?.arquivo ? (
                    <AvatarImage src={resolveMediaUrl(ouvidoria.logo.arquivo) || ouvidoria.logo.arquivo} alt={ouvidoria.nome} />
                  ) : null}
                  <AvatarFallback className="text-2xl bg-gradient-to-br from-primary/20 to-secondary/20">
                    {avatarFallback}
                  </AvatarFallback>
                </Avatar>
                
                <h2 className="text-xl font-semibold">{ouvidoria.nome || "Nome não informado"}</h2>
                {ouvidoria.nomeFantasia && (
                  <p className="text-sm text-muted-foreground">{ouvidoria.nomeFantasia}</p>
                )}
                
                <div className="flex items-center gap-2 mt-2">
                  <Badge
                    variant={ouvidoria.arquivada ? "destructive" : "default"}
                    className={cn(
                      !ouvidoria.arquivada && "bg-success/10 text-success border-success/20"
                    )}
                  >
                    {ouvidoria.arquivada ? (
                      <>
                        <XCircle className="h-3 w-3 mr-1" />
                        Arquivada
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Ativa
                      </>
                    )}
                  </Badge>
                  {ouvidoria.geral && (
                    <Badge variant="secondary">
                      <Shield className="h-3 w-3 mr-1" />
                      Geral
                    </Badge>
                  )}
                </div>

                <Separator className="my-4" />

                <div className="w-full space-y-3 text-left">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">CNPJ</p>
                      <p className="text-sm font-medium">{ouvidoria.cnpj || "Não informado"}</p>
                    </div>
                  </div>
                  
                  {ouvidoria.razaoSocial && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Razão Social</p>
                        <p className="text-sm font-medium">{ouvidoria.razaoSocial}</p>
                      </div>
                    </div>
                  )}
                  
                  {ouvidoria.email && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Mail className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">E-mail</p>
                        <p className="text-sm font-medium">{ouvidoria.email}</p>
                      </div>
                    </div>
                  )}
                  
                  {ouvidoria.telefone && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Phone className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Telefone</p>
                        <p className="text-sm font-medium">{ouvidoria.telefone}</p>
                      </div>
                    </div>
                  )}

                  {ouvidoria.etiquetas && ouvidoria.etiquetas.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {ouvidoria.etiquetas.map((etiqueta, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {etiqueta}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle>Informações da Ouvidoria</CardTitle>
              <CardDescription>Detalhes completos da ouvidoria supervisionada</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {ouvidoria.endereco && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <MapPin className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Endereço</p>
                      <p className="text-sm font-medium">
                        {ouvidoria.endereco.logradouro}, {ouvidoria.endereco.numero}
                        {ouvidoria.endereco.complemento && ` - ${ouvidoria.endereco.complemento}`}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {ouvidoria.endereco.bairro} - {ouvidoria.endereco.cidade}/{ouvidoria.endereco.estado}
                      </p>
                      <p className="text-sm text-muted-foreground">CEP: {ouvidoria.endereco.cep}</p>
                    </div>
                  </div>
                )}
                
                {(ouvidoria.latitude || ouvidoria.longitude) && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Globe className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Coordenadas</p>
                      <p className="text-sm font-medium">
                        Latitude: {ouvidoria.latitude?.toFixed(6)} | Longitude: {ouvidoria.longitude?.toFixed(6)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Solicitações Atribuídas - Full Width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Solicitações Atribuídas
            </CardTitle>
            <CardDescription>
              Solicitações atribuídas a esta ouvidoria supervisionada
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingSolicitacoes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : solicitacoes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground/50 mb-2" />
                <p className="text-muted-foreground">Nenhuma solicitação atribuída</p>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {solicitacoes.map((solicitacao) => {
                  const primeiroAnexo = solicitacao.anexos?.[0]?.arquivo;
                  
                  // Mapear status da API para o tipo StatusSolicitacao
                  const mapStatus = (status: string): StatusSolicitacao => {
                    const statusLower = status.toLowerCase();
                    if (statusLower === "emanalise" || statusLower === "em analise") return "EmAnalise";
                    if (statusLower === "resolvida" || statusLower === "concluida") return "resolvida";
                    if (statusLower === "agendada") return "agendada";
                    if (statusLower === "aprovada") return "aprovada";
                    if (statusLower === "reprovada") return "reprovada";
                    if (statusLower === "arquivada") return "arquivada";
                    return "pendente";
                  };
                  
                  return (
                    <div
                      key={solicitacao.id}
                      className="flex gap-3 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={() => handleSolicitacaoClick(solicitacao.id)}
                    >
                      {/* Thumbnail */}
                      <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                        {primeiroAnexo ? (
                          <img 
                            src={primeiroAnexo} 
                            alt="Anexo" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-medium text-sm truncate">
                            {solicitacao.upn}
                          </span>
                          <SolicitacaoStatusBadge 
                            status={mapStatus(solicitacao.status)} 
                            estadoAprovacao={solicitacao.estadoAprovacao}
                            className="shrink-0"
                          />
                        </div>
                        <Badge variant="outline" className="text-xs w-fit mb-2">
                          {solicitacao.topico?.descricao || "Sem tópico"}
                        </Badge>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(solicitacao.dataCriacao).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
